import React from 'react';
import styled from '@emotion/styled';
import BookingBlockItem from './BookingBlockItem';
import IncomingBookingBlockItem from './IncomingBookingBlockItem';
import { Cheque } from '@baemingo/table-events/dist/types';
import ChequeBlockItem from './ChequeBlockItem';
import {
  GetBookings_operativeViewer_bookingsForVenueAndDate,
  GetIncomingBookings_operativeViewer_incomingBookingsForVenue,
} from '~Providers/BookingsProvider/api/__queries__';
import useI18n from '~Translations';
import { getBookingPosition, getChequePosition } from '../utils';
import { useBookings } from '~Providers/BookingsProvider';
import { BookingStatus } from '../../../../__queries__';

const TableLabel = styled.div`
  padding: 8px;
  color: var(--colorWhite);
  font-size: 14px;
  font-weight: 600;
  grid-row: 1;
  background-color: var(--colorBlueGray1100);
  position: sticky;
  left: 0;
  z-index: 10;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimelineRow = styled.div<{ columnCount: number; hasOverlap?: boolean }>`
  display: grid;
  grid-template-columns: 64px repeat(${(props) => props.columnCount}, 60px);
  grid-template-rows: 1fr;
  gap: 1px;
  position: relative;
  min-height: 40px;
  border-bottom: 1px solid var(--colorBlueGray1100);
  min-width: min-content;
  background-color: var(--colorBlueGray1200);
  opacity: ${(props) => (props.hasOverlap ? 0.5 : 1)};
  transition: opacity 0.2s;
`;

const TimeSlotCell = styled.div<{ isBookingStartTime?: boolean }>`
  background-color: var(--colorBlueGray1200);
  min-height: 40px;
  grid-row: 1;
  border-right: 1px solid var(--colorBlueGray1100);
  border-left: ${(props) =>
    props.isBookingStartTime ? '3px solid var(--colorUIOrange)' : 'none'};
  position: relative;
`;

const BookingName = styled.div`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface Props {
  table: { id: string; code: string };
  timeSlots: string[];
  timeRange: { startHour: number; endHour: number };
  bookingsForTable: GetBookings_operativeViewer_bookingsForVenueAndDate[];
  incomingBookings?: GetIncomingBookings_operativeViewer_incomingBookingsForVenue[]; // Incoming bookings with 50% opacity
  ongoingCheques: Cheque[];
  onChequeClick: (chequeId: string, tableId: string) => void;
  onBookingClick: (booking: GetBookings_operativeViewer_bookingsForVenueAndDate) => void;
  bookingToHighlight?: string | null;
  onHighlightComplete?: () => void;
  currentTime?: Date;
  selectedChequeId?: string | null;
  selectedBookingId?: string | null;
}

const TableRow: React.FC<Props> = ({
  table,
  timeSlots,
  timeRange,
  bookingsForTable,
  incomingBookings,
  ongoingCheques,
  onChequeClick,
  onBookingClick,
  bookingToHighlight,
  onHighlightComplete,
  currentTime,
  selectedChequeId,
  selectedBookingId,
}) => {
  const { i18n } = useI18n();
  const bookings = useBookings();

  const acceptedBookings = bookingsForTable.filter(
    (booking) => booking.status === BookingStatus.ACCEPTED,
  );
  const startedBookings = bookingsForTable.filter(
    (booking) => booking.status === BookingStatus.STARTED,
  );

  // Filter overlapping cheques based on priority:
  // For overlapping cheques: 1. Cheque with booking connection (highest priority) 2. First cheque if no booking connection exists
  // For example if a booking+cheque is about to close and they split the cheque into multiple cheques we don't want to crowd the timeline with cheques that are about to be paid and only living for a couple of minutes
  const tableCheques = React.useMemo(() => {
    if (ongoingCheques.length === 0) return [];
    if (ongoingCheques.length === 1) return ongoingCheques;

    // Check all bookings (accepted and started) for cheque connections
    const allBookings = [...acceptedBookings, ...startedBookings];

    // Group cheques by overlapping time periods
    const chequesToShow: typeof ongoingCheques = [];
    const processedIndices = new Set<number>();

    ongoingCheques.forEach((cheque, index) => {
      if (processedIndices.has(index) || !cheque.activeAt) return;

      // Find all cheques that overlap with this one
      const overlappingGroup = [cheque];
      const overlappingIndices = [index];

      const chequeStart = new Date(cheque.activeAt).getTime();
      const chequeEnd = cheque.bookingData?.endsAt
        ? new Date(cheque.bookingData.endsAt).getTime()
        : chequeStart + bookings.defaultDurationMinutes * 60 * 1000;

      ongoingCheques.forEach((otherCheque, otherIndex) => {
        if (otherIndex === index || !otherCheque.activeAt || processedIndices.has(otherIndex))
          return;

        const otherStart = new Date(otherCheque.activeAt).getTime();
        const otherEnd = otherCheque.bookingData?.endsAt
          ? new Date(otherCheque.bookingData.endsAt).getTime()
          : otherStart + bookings.defaultDurationMinutes * 60 * 1000;

        // Check for overlap
        if (
          (chequeStart >= otherStart && chequeStart < otherEnd) ||
          (chequeEnd > otherStart && chequeEnd <= otherEnd) ||
          (chequeStart <= otherStart && chequeEnd >= otherEnd)
        ) {
          overlappingGroup.push(otherCheque);
          overlappingIndices.push(otherIndex);
        }
      });

      // Mark all in group as processed
      overlappingIndices.forEach((i) => processedIndices.add(i));

      if (overlappingGroup.length === 1) {
        // No overlap, show this cheque
        chequesToShow.push(cheque);
      } else {
        // Multiple overlapping cheques, apply priority
        // Find if any cheque in the group has a booking connection
        const chequeWithBooking = overlappingGroup.find((c) =>
          allBookings.some((booking) => booking.chequeId === c.id),
        );

        if (chequeWithBooking) {
          chequesToShow.push(chequeWithBooking);
        } else {
          // No booking connection, show the first one
          chequesToShow.push(overlappingGroup[0]);
        }
      }
    });

    return chequesToShow;
  }, [ongoingCheques, acceptedBookings, startedBookings]);

  return (
    <TimelineRow columnCount={timeSlots.length}>
      <TableLabel>{table.code}</TableLabel>
      {timeSlots.map((time) => {
        return <TimeSlotCell key={time} />;
      })}

      {/* Overlay ongoing cheque blocks (walk-ins) */}
      {tableCheques.map((cheque) => {
        if (!cheque.activeAt) return null;

        // Find if this cheque matches a booking by chequeId
        // Check both accepted bookings (shouldn't happen) and started bookings
        const allBookings = [...acceptedBookings, ...(startedBookings || [])];
        const matchingBooking = allBookings.find((booking) => booking.chequeId === cheque.id);

        // Use booking guest name if there's a match, fall back to cheque.bookingData, then cheque name or 'Walk-in'
        const chequeBookingName = cheque.bookingData
          ? [cheque.bookingData.firstName, cheque.bookingData.lastName].filter(Boolean).join(' ').trim()
          : null;
        const customerName =
          matchingBooking?.guestName || chequeBookingName || cheque.name || i18n('Bookings.WalkIn');

        let gridColumnStart: number;
        let offsetMinutes: number;
        let isPastDue: boolean;
        let durationMinutes: number;

        if (matchingBooking) {
          // Parse booking times to calculate original duration
          const [bookingStartHours, bookingStartMinutes] = matchingBooking.startTime
            .split(':')
            .map(Number);
          const [bookingEndHours, bookingEndMinutes] = matchingBooking.endTime
            .split(':')
            .map(Number);
          const bookingStartTotalMinutes = bookingStartHours * 60 + bookingStartMinutes;
          const bookingEndTotalMinutes = bookingEndHours * 60 + bookingEndMinutes;
          const bookingDuration = bookingEndTotalMinutes - bookingStartTotalMinutes;

          // Example: If booking was 17:00-18:30 (90 min) and guest arrived at 16:30,
          // the adjusted end time would be 16:30 + 90 min = 18:00

          // Use getChequePosition for start position
          const position = getChequePosition({
            startTime: cheque.activeAt,
            currentTime,
            timeRange,
          });
          gridColumnStart = position.gridColumnStart;
          offsetMinutes = position.offsetMinutes;
          isPastDue = position.isPastDue;

          // For bookings, calculate the actual duration to display
          if (isPastDue && currentTime) {
            // If past due, extend to current time
            const chequeStartDate = new Date(cheque.activeAt);
            const chequeStartMinutes =
              chequeStartDate.getHours() * 60 + chequeStartDate.getMinutes();
            const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
            durationMinutes = currentMinutes - chequeStartMinutes;
          } else {
            // Use booking duration
            durationMinutes = bookingDuration;
          }
        } else {
          // For cheques without a matching backend booking, check if bookingData.endsAt exists
          // to calculate a more accurate duration instead of assuming default
          let chequeDefaultDuration = bookings.defaultDurationMinutes;

          if (cheque.bookingData?.endsAt) {
            const chequeStart = new Date(cheque.activeAt);
            const chequeEnd = new Date(cheque.bookingData.endsAt);
            const calculatedDuration = (chequeEnd.getTime() - chequeStart.getTime()) / (60 * 1000);
            if (calculatedDuration > 0) {
              chequeDefaultDuration = calculatedDuration;
            }
          }

          const position = getChequePosition({
            startTime: cheque.activeAt,
            currentTime,
            timeRange,
            defaultDurationMinutes: chequeDefaultDuration,
          });
          gridColumnStart = position.gridColumnStart;
          offsetMinutes = position.offsetMinutes;
          isPastDue = position.isPastDue;
          durationMinutes = position.durationMinutes;
        }

        return (
          <ChequeBlockItem
            key={cheque.id}
            chequeId={cheque.id}
            tableId={table.id}
            gridColumnStart={gridColumnStart}
            offsetMinutes={offsetMinutes}
            durationMinutes={durationMinutes}
            isSelected={selectedChequeId ? matchingBooking?.chequeId === selectedChequeId : false}
            isPastDue={isPastDue}
            hasMultipleTables={matchingBooking ? matchingBooking.tableIds.length > 1 : false}
            onChequeClick={onChequeClick}>
            <BookingName>
              {customerName} -{' '}
              {matchingBooking?.partySize ||
                cheque.bookingData?.numberOfGuests ||
                cheque.coverage ||
                0}{' '}
              guests
            </BookingName>
          </ChequeBlockItem>
        );
      })}

      {/* Overlay booking blocks - using startTime and endTime for positioning */}
      {acceptedBookings
        .filter((booking) => !booking.chequeId) // Don't show bookings that have been started (they appear as cheques)
        .map((booking) => {
          const { gridColumnStart, gridColumnSpan, offsetMinutes } = getBookingPosition({
            startTime: booking.startTime,
            endTime: booking.endTime,
            timeRange,
          });
          const isHighlighted = bookingToHighlight === booking.id;

          return (
            <BookingBlockItem
              key={booking.id}
              booking={booking}
              gridColumnStart={gridColumnStart}
              gridColumnSpan={gridColumnSpan}
              offsetMinutes={offsetMinutes}
              onClick={() => onBookingClick(booking)}
              isHighlighted={isHighlighted}
              isSelected={selectedBookingId ? booking.id === selectedBookingId : false}
              onHighlightComplete={isHighlighted ? onHighlightComplete : undefined}
            />
          );
        })}

      {/* Overlay incoming booking blocks with 50% opacity */}
      {incomingBookings?.map((booking) => {
        const { gridColumnStart, gridColumnSpan, offsetMinutes } = getBookingPosition({
          startTime: booking.startTime,
          endTime: booking.endTime,
          timeRange,
        });

        return (
          <IncomingBookingBlockItem
            key={booking.id}
            booking={booking}
            gridColumnStart={gridColumnStart}
            gridColumnSpan={gridColumnSpan}
            offsetMinutes={offsetMinutes}
          />
        );
      })}
    </TimelineRow>
  );
};

export default TableRow;
